<template>
  <v-row v-if="isEnabled">
    <v-col cols="12">
      <h2>
        {{ $t("Jätä palaute tai kysymys") }}
      </h2>
    </v-col>
    <v-col lg="8" sm="12" cols="12">
      <v-card class="mx-auto">
        <div v-if="sent" class="pa-5">
          <v-alert type="success">
            {{ $t("Kiitos, viesti lähetetty") }}
          </v-alert>
        </div>
        <div v-else-if="isLoading" class="pa-6 text-center">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <v-form ref="form" @submit.prevent="submit" v-model="valid" lazy-validation v-else>
          <br />
          <v-card-text class="text--primary mb-0 pb-0">

            <template v-if="categories.length">
              <div class="v-list-item__title"><b>{{ $t('Valitse palautteen tyyppi') }}</b></div>

              <v-list>
                <v-list-item-group v-model="selectedCategoryId" color="primary">
                  <v-list-item v-for="cat in categories" :key="cat.id" :value="cat.id" class="my-2">
                    <v-list-item-content>
                      <v-list-item-title>{{ cat.title }}</v-list-item-title>
                      <v-list-item-subtitle>{{ cat.description }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </template>

            <template v-if="!categories.length || selectedCategoryId">

              <v-alert v-if="selectedCategoryGuide" class="mt-3" border="left" outlined type="info"
                style="white-space: pre-line;">
                {{ selectedCategoryGuide }}
              </v-alert>

              <!-- Loading state for form -->
              <div v-if="isLoadingForm" class="text-center my-4">
                <v-progress-circular indeterminate size="32"></v-progress-circular>
                <div class="mt-2 text-caption">{{ $t('Ladataan lomaketta...') }}</div>
              </div>

              <!-- Form mode: show FormRenderer when category has form enabled -->
              <div v-else-if="categoryForm">
                <FormRenderer ref="formRendererRef" :form="categoryForm" v-model="formResponses" />
              </div>

              <!-- Text mode: show textarea when no form -->
              <v-textarea v-else outlined required :rules="[required]" v-model="feedback"
                :label="$t('Kirjoita palaute tai kysymys')" class="mt-3"></v-textarea>

              <v-card outlined class="mt-3">
                <v-card-text>
                  <span class="subtitle-1">{{ $t('Liitteet') }}</span>
                  <v-list dense>
                    <div v-for="attachment in attachments" :key="attachment.id">
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon color="primary">
                            {{ icons.mdiAttachment }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          {{ attachment.name }}
                        </v-list-item-content>
                        <v-list-item-icon @click="removeAttachment(attachment.id)" style="cursor: pointer">
                          <v-icon color="secondary">
                            {{ icons.mdiDeleteOutline }}
                          </v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                      <v-divider></v-divider>
                    </div>
                  </v-list>

                  <v-file-input :loading="isSavingAttachment" ref="attachmentInput" persistent-hint outlined
                    :rules="attachmentRules" @change="saveAttachment"
                    :label="$t('Halutessasi voit liittää mukaan kuvan tai pdf-tiedoston')" accept="image/*, .pdf">
                  </v-file-input>

                </v-card-text>
              </v-card>

              <v-switch v-model="require_response"
                :label="$t('Haluatko jättää yhteystietosi henkilökohtaista vastausta varten?')"></v-switch>
              <v-text-field outlined hide-details="auto" v-if="require_response" v-model="response_channel"
                :label="$t('Anna sähköposti tai puhelinnumero')" :rules="response_channel_rules">
              </v-text-field>
              <br />
              <v-btn :disabled="isSavingAttachment" color="primary" type="submit">{{ $t("Lähetä") }}</v-btn>
            </template>
            <br /><br />
          </v-card-text>
        </v-form>
        <div class="grey--text ml-4 mr-4"></div>
      </v-card>
    </v-col>
    <v-col lg="4" sm="12" cols="12">
      <v-card class="mx-auto mb-4" v-if="!user.read_only">
        <v-card-title>
          {{ $t("Palautteet") }}
        </v-card-title>
        <v-card-text>
          <v-btn v-if="!feedbacksVisible" @click="showArchive" color="primary" block>

            {{ $t('Näytä lähetetyt palautteet') }}
          </v-btn>
          <v-btn v-else @click="hideArchive" color="primary" block>
            {{ $t('Piilota lähetetyt palautteet') }}

          </v-btn>
          <br>
          <div v-if="feedbacksVisible">
            <v-list v-if="feedbacks.length">
              <v-list-item v-for="f in feedbacks" :to="{ name: 'feedback-details', params: { id: f.id } }" :key="f.id">
                <v-list-item-content>
                  <v-list-item-title>{{ getDate(f.insert_date) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ f.completed ? $t('Käsitelty') : $t('Avoin') }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="f.completed"><v-icon color="success">{{ mdiCheckCircleOutline
                    }}</v-icon></v-list-item-action>
              </v-list-item>
            </v-list>

            <!-- Pagination controls -->
            <v-pagination v-if="totalPages > 1" v-model="currentPage" :length="totalPages" :total-visible="5"
              @input="loadPage" class="mt-3"></v-pagination>

            <div v-if="!feedbacks.length" class="text-center pa-4 grey--text">
              {{ $t('Ei lähetettyjä palautteita') }}
            </div>
          </div>
        </v-card-text>
      </v-card>
      <v-card class="mx-auto">
        <v-card-title>
          {{ $t("Miten palautteet käsitellään?") }}
        </v-card-title>
        <v-card-text style="white-space: pre-line">
          {{ policy }}
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import { useTranslations } from "@/@core/libs/i18n";
import api from "@/api";
import useAuth from "@/useAuth";
import { getDateTime, getDate } from "@/@core/utils/datetime_helpers";
import { required } from "@/@core/utils/validation";
import { i18n } from "@/plugins/i18n";
import { mdiCheckCircleOutline, mdiAttachment, mdiDeleteOutline } from "@mdi/js";
import FormRenderer from "@/components/forms/FormRenderer.vue";

export default {
  components: { FormRenderer },
  setup() {
    const { user, tenant } = useAuth();
    const attachmentInput = ref(null);
    const feedbacks = ref([]);
    const fetchedFeedbacks = ref(false);
    const feedbacksVisible = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const totalCount = ref(0);
    const pageSize = 20;
    const form = ref(null);
    const feedback = ref("");
    const selectedCategoryId = ref(null);
    const policy = ref("");
    const require_response = ref(false);
    const response_channel = ref("");
    const sent = ref(false);
    const valid = ref(false);
    const attachment = ref(null);
    const attachment_name = ref('');
    const isLoading = ref(false);
    const categories = ref([]);
    const isSavingAttachment = ref(false);
    const attachments = ref([]);

    // Form-related refs
    const categoryForm = ref(null);
    const isLoadingForm = ref(false);
    const formResponses = ref({});
    const formRendererRef = ref(null);

    const supportedFileExtensions = ['jpg', 'jpeg', 'heic', 'heif', 'png', 'webp', 'pdf'];
    const { t } = useTranslations();
    const response_channel_rules = [
      (v) => !!v ||
        !require_response.value ||
        i18n.t("Anna sähköpostiosoite tai puhelinnumero"),
    ];

    const attachmentRules = [
      v => {
        if (!v)
          return true;
        let extension = v.type.split('/').pop() ?? "";
        extension = extension.toLowerCase();
        if (supportedFileExtensions.indexOf(extension) < 0)
          return i18n.t("Tiedostomuoto ei ole tuettu. Tuettuja tiedostomutooja ovat ") + supportedFileExtensions.join(', ');

        return true;
      }
    ];

    const handleFileUpload = (file) => {
      attachment.value = file;
      attachment_name.value = file ? file.name : null;
    };

    const saveAttachment = async (file) => {
      if (file && attachmentInput.value.validate()) {
        isSavingAttachment.value = true;
        const response = await api.saveFeedbackAttachment(file);

        if (response.status == 201) {
          attachments.value = [response.data, ...attachments.value];
        }
        else {
          alert(response.error);
        }
        attachmentInput.value.reset();
        isSavingAttachment.value = false;
      }
    };

    const removeAttachment = async (id) => {
      const response = await api.deleteFeedbackAttachment(id);
      if (response.ok) {
        attachments.value = attachments.value.filter(a => a.id != id);
      }
      else {
        alert("Liitteen poistaminen epäonnistui");
      }
    };


    const fetchCategories = async () => {
      isLoading.value = true;
      const response = await api.getFeedbackCategories();
      if (response.ok) {
        categories.value = response.data;
      }
      isLoading.value = false;
    }

    const fetchPolicy = async () => {
      const response = await api.getFeedbackPolicy();
      if (response.ok) {
        policy.value = response.data.feedback_policy;
      }
    };

    // Fetch form when category with use_form is selected
    const fetchCategoryForm = async (categoryId) => {
      if (!categoryId) {
        categoryForm.value = null;
        return;
      }

      const category = categories.value.find(c => c.id === categoryId);
      if (!category || !category.use_form) {
        categoryForm.value = null;
        return;
      }

      isLoadingForm.value = true;
      try {
        const response = await api.getCategoryForm(categoryId);
        if (response.ok) {
          // Backend returns only { fields: [...] } for feedback category forms
          categoryForm.value = {
            fields: response.data?.fields ?? []
          };
        } else {
          categoryForm.value = null;
        }
      } catch (error) {
        console.error('Failed to load form:', error);
        categoryForm.value = null;
      }
      isLoadingForm.value = false;
    };

    // Watch for category selection changes
    watch(selectedCategoryId, (newCategoryId) => {
      // Reset form data when category changes
      categoryForm.value = null;
      formResponses.value = {};
      feedback.value = "";

      if (newCategoryId) {
        fetchCategoryForm(newCategoryId);
      }
    });

    const submit = async () => {
      // Validate the form - FormRenderer handles its own validation
      if (categoryForm.value) {
        // Form mode: validate using FormRenderer and get responses
        if (!formRendererRef.value || !formRendererRef.value.validate()) {
          return;
        }

        const responses = formRendererRef.value.getFormattedResponses();
        const response = await api.saveFeedback({
          require_answer: require_response.value,
          answer_to: response_channel.value ?? "",
          attachment: null,
          attachment_name: null,
          category: selectedCategoryId.value,
          attachments: attachments.value.map(a => a.id),
          responses: responses
        });

        if (response.ok) {
          sent.value = true;
        } else {
          alert(response.error);
        }
      } else {
        // Text mode: use original validation
        if (!form.value.validate()) {
          return;
        }

        const response = await api.saveFeedback({
          feedback: feedback.value,
          require_answer: require_response.value,
          answer_to: response_channel.value ?? "",
          attachment: null,
          attachment_name: null,
          category: selectedCategoryId.value,
          attachments: attachments.value.map(a => a.id)
        });

        if (response.ok) {
          sent.value = true;
        } else {
          alert(response.error);
        }
      }
    };

    const loadPage = async (page) => {
      const res = await api.getMyFeedbacks(page, pageSize);
      if (res.ok) {
        feedbacks.value = res.data.results;
        totalCount.value = res.data.count;
        totalPages.value = Math.ceil(res.data.count / pageSize);
      }
    }

    const showArchive = async () => {
      if (fetchedFeedbacks.value) {
        feedbacksVisible.value = true;
        return;
      }

      await loadPage(1);
      fetchedFeedbacks.value = true;
      feedbacksVisible.value = true;
    }

    const hideArchive = () => {
      feedbacksVisible.value = false;
    }

    const selectedCategory = computed(() => {
      if (!selectedCategoryId.value) return null;
      const cat = categories.value.find(a => a.id == selectedCategoryId.value);
      return cat;
    });

    const selectedCategoryDescription = computed(() => {
      if (selectedCategory.value == null) return '';

      return selectedCategory.value.description ?? "";
    });

    const selectedCategoryGuide = computed(() => {
      if (selectedCategory.value == null || !selectedCategory.value.guide) return '';

      return selectedCategory.value.guide;
    });

    const isEnabled = computed(() => {
      return tenant.value.settings.feedback_enabled;
    });

    onMounted(() => {
      fetchCategories();
      fetchPolicy();
    });

    return {
      isEnabled,
      feedback,
      require_response,
      policy,
      attachment,
      form,
      selectedCategoryId,
      submit,
      response_channel,
      response_channel_rules,
      attachments,
      isSavingAttachment,
      attachmentRules,
      handleFileUpload,
      saveAttachment,
      feedbacksVisible,
      isLoading,
      isLoadingForm,
      hideArchive,
      required,
      mdiCheckCircleOutline,
      sent,
      selectedCategoryGuide,
      removeAttachment,
      categories,
      categoryForm,
      formResponses,
      formRendererRef,
      user,
      selectedCategoryDescription,
      showArchive,
      getDate,
      attachmentInput,
      feedbacks,
      valid,
      t,
      currentPage,
      totalPages,
      totalCount,
      loadPage,
      icons: {
        mdiAttachment,
        mdiCheckCircleOutline,
        mdiDeleteOutline
      }
    };
  }
};
</script>

<style></style>
